<template>
  <div class="flex w-full h-full">
    <ElementsGraph
      :elements="elements"
      @addElement="addChild"
      @updateNodeFigure="updateElementFigure"
      @figure-click="switchToFigureAnnotator"
      @element-click="openElementDetails"
    />
    <div
      class="h-full w-100 bg-white px-6 py-4 shadow-lg"
      v-if="activeElementId"
    >
      <div class="flex justify-between">
        <div>{{ activeElement?.name }}</div>
        <button @click="activeElementId = ''" class="px-2">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
      <div class="py-2 flex flex-col items-start gap-4">
        <template v-if="activeElementId !== '1'">
          <TextInput
            :model-value="activeElement?.name"
            @update:model-value="
              (name) => debouncedUpdateElementName(activeElementId, name)
            "
          />
          <FloatingMenu :offset="[0, 10]">
            <template #activator="{ show }">
              <button
                class="text-red-800 flex items-center gap-1 border-red-800 border px-2 py-1 self-end bg-red-50 hover:text-white hover:bg-red-800"
                @click="show"
              >
                Delete element
                <span class="material-symbols-outlined">delete</span>
              </button>
            </template>
            <template #content="{ hide }">
              <div class="p-4 flex flex-col gap-2 text-dark-neutral-500">
                Are you sure you want to delete this element and all it's sub
                items?
                <div class="flex gap-2">
                  <button
                    class="text-red-800 border-red-800 border px-2 py-1 bg-red-50 hover:text-white hover:bg-red-800"
                    @click="deleteElement(activeElementId)"
                  >
                    Delete
                  </button>
                  <button
                    class="text-gray-600 border-gray-600 border px-2 py-1 bg-gray-50 hover:text-white hover:bg-gray-600"
                    @click="hide"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </template>
          </FloatingMenu>
        </template>
        <template v-else>
          This is the root element and cannot be edited or deleted.
        </template>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import ElementsGraph from "./ElementsGraph.vue";
import { useRouter } from "vue-router";
import { RouteName } from "router";
import { useFunctionalElementsState } from "states/functionalElements";
import { computed, ref } from "vue";
import { generateId, getFlattenedSubtree } from "utils";
import { FloatingMenu, TextInput } from "ui-components";
import { useDebounceFn } from "@vueuse/core";

const elements = useFunctionalElementsState();
const addChild = (parentId: string) => {
  elements.value.push({
    id: generateId(),
    name: "Element " + elements.value.length,
    parentElementId: parentId,
  });
};

function updateElementFigure(elementId: string, file: File) {
  const reader = new FileReader();
  reader.onload = async (event) => {
    if (event.target?.result) {
      const imageUrl = event.target.result as string;
      const element = elements.value.find((el) => el.id === elementId);
      if (element) {
        element.figureUrl = imageUrl;
      }
    }
  };
  reader.readAsDataURL(file);
}
const router = useRouter();
function switchToFigureAnnotator(elementId: string) {
  router.push({
    name: RouteName.FUNCTIONAL_ELEMENTS_FIGURE,
    params: { id: elementId },
  });
}
const activeElementId = ref<string>("");
function openElementDetails(elementId: string) {
  activeElementId.value = elementId;
}

const activeElement = computed(() =>
  elements.value.find((el) => el.id === activeElementId.value)
);

const debouncedUpdateElementName = useDebounceFn(updateElementName, 300);
function updateElementName(elementId: string, name: string) {
  const element = elements.value.find((el) => el.id === elementId);
  if (element) {
    element.name = name;
  }
}

function deleteElement(elementId: string) {
  const toRemove = getFlattenedSubtree(elements.value, elementId);
  const result = [...elements.value].filter(
    (el) => !toRemove.some((e) => e.id === el.id)
  );
  elements.value = JSON.parse(JSON.stringify(result));
  activeElementId.value = "";
}
</script>
